import React, { useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "antd";
import DataQButton from "shared/ui/Buttons/DataQButton.component";
import { selectConversation, useAssistantStore } from "../assistantStore";
import { useAssistantScreen } from "../screenRegistry";
import { useAssistantStatus } from "../useAssistant";

/** The tooltip of the greyed-out toggle, for an organisation without the assistant. */
export const ASSISTANT_OFF_TEXT =
  "The AI assistant isn't switched on for your organisation. Ask your DATA(Q) contact to turn it on.";

/**
 * The state behind an assistant toggle for `projectId`: `visible` once the
 * status is known (nothing shows while it loads), `available` only when the
 * user's organisation has the assistant (otherwise the toggle is greyed out
 * with ASSISTANT_OFF_TEXT), whether the panel is `open`, and `toggle`. When
 * the status comes back off (e.g. after a `disabled` turn) an open panel is
 * closed.
 */
export const useAssistantToggle = (
  projectId: string,
): {
  visible: boolean;
  available: boolean;
  open: boolean;
  toggle: () => void;
} => {
  const { enabled, isFetched } = useAssistantStatus();
  const open = useAssistantStore((s) => selectConversation(projectId)(s).open);
  const setOpen = useAssistantStore((s) => s.setOpen);
  const toggleOpen = useAssistantStore((s) => s.toggleOpen);

  useEffect(() => {
    if (isFetched && !enabled && open) setOpen(projectId, false);
  }, [isFetched, enabled, open, setOpen, projectId]);

  const toggle = useCallback(() => {
    if (enabled) toggleOpen(projectId);
  }, [enabled, toggleOpen, projectId]);
  return {
    visible: isFetched || enabled,
    available: enabled,
    open: enabled && open,
    toggle,
  };
};

/**
 * Opens and closes the assistant panel of the current project. Greyed out,
 * with ASSISTANT_OFF_TEXT as its tooltip, when the user's organisation does
 * not have the assistant; nothing while the status loads.
 */
const AssistantToggle: React.FC = () => {
  const { projectId } = useAssistantScreen();
  const { visible, available, open, toggle } = useAssistantToggle(projectId);
  if (!visible) return null;
  if (!available) {
    return (
      <Tooltip title={ASSISTANT_OFF_TEXT} placement="bottom">
        {/* A disabled button gets no mouse events, so the span carries the tooltip. */}
        <span className="inline-flex">
          <DataQButton
            type="secondary"
            isDisabled
            aria-label="Assistant (not switched on)"
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} />
            Assistant
          </DataQButton>
        </span>
      </Tooltip>
    );
  }
  return (
    <DataQButton
      type="secondary"
      onClick={toggle}
      aria-label={open ? "Close the assistant" : "Open the assistant"}
    >
      <FontAwesomeIcon icon={faWandMagicSparkles} />
      Assistant
    </DataQButton>
  );
};

export default AssistantToggle;
